import React, { useEffect, useState } from "react";
import { Tooltip } from "antd";
import { Card, CardContent } from "../ui/card";
import { cn } from "../../lib/utils";
import { CriterionBreakdown, EvaluationType } from "../../model_gen/evaluation";

export const SCORE_META = [
  { score: 1, label: "Poor", color: "#ef4444" },
  { score: 2, label: "Fair", color: "#f97316" },
  { score: 3, label: "Good", color: "#a3a3a3" },
  { score: 4, label: "Great", color: "#84cc16" },
  { score: 5, label: "Perfect", color: "#22c55e" },
];

const DETERMINISTIC_TYPES = new Set<string>([
  EvaluationType.EXACT_MATCH,
  EvaluationType.CONTAINS,
  EvaluationType.BLEU,
  EvaluationType.ROUGE,
  EvaluationType.COSINE_SIMILARITY,
  EvaluationType.LEVENSHTEIN,
]);

interface Props {
  breakdowns: CriterionBreakdown[];
}

function DeterministicView({ bd }: { bd: CriterionBreakdown }) {
  const passRate =
    bd.total_evaluated > 0 ? (bd.pass_count / bd.total_evaluated) * 100 : 0;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-6">
        <div className="space-y-1">
          <div className="text-xs text-muted-foreground font-gc-reg uppercase tracking-wider">
            Pass Rate
          </div>
          <div className="text-2xl font-gc-demi-bold text-foreground">
            {passRate.toFixed(1)}%
          </div>
          <div className="text-xs text-muted-foreground font-gc-reg">
            {bd.pass_count.toLocaleString()} / {bd.total_evaluated.toLocaleString()} passed
          </div>
        </div>
        {bd.avg_numeric_score != null && (
          <div className="space-y-1">
            <div className="text-xs text-muted-foreground font-gc-reg uppercase tracking-wider">
              Avg Score
            </div>
            <div className="text-2xl font-gc-demi-bold text-foreground">
              {bd.avg_numeric_score.toFixed(4)}
            </div>
            <div className="text-xs text-muted-foreground font-gc-reg">
              numeric (0–1)
            </div>
          </div>
        )}
        <div className="space-y-1">
          <div className="text-xs text-muted-foreground font-gc-reg uppercase tracking-wider">
            Failures
          </div>
          <div className="text-2xl font-gc-demi-bold text-foreground">
            {bd.fail_count.toLocaleString()}
          </div>
          <div className="text-xs text-muted-foreground font-gc-reg">
            did not pass threshold
          </div>
        </div>
      </div>

      {/* Pass/fail bar */}
      <div className="space-y-1">
        <div className="flex rounded-md overflow-hidden h-6">
          {bd.pass_count > 0 && (
            <Tooltip title={`Passed: ${bd.pass_count.toLocaleString()} (${passRate.toFixed(1)}%)`}>
              <div
                className="h-full transition-all duration-300"
                style={{
                  width: `${passRate}%`,
                  backgroundColor: "#22c55e",
                }}
              />
            </Tooltip>
          )}
          {bd.fail_count > 0 && (
            <Tooltip title={`Failed: ${bd.fail_count.toLocaleString()} (${(100 - passRate).toFixed(1)}%)`}>
              <div
                className="h-full transition-all duration-300"
                style={{
                  width: `${100 - passRate}%`,
                  backgroundColor: "#ef4444",
                }}
              />
            </Tooltip>
          )}
        </div>
        <div className="flex items-center justify-center gap-4 text-xs">
          <span className="flex items-center gap-1.5 text-muted-foreground font-gc-reg">
            <span className="w-2.5 h-2.5 rounded-full inline-block bg-green-500" />
            Pass
          </span>
          <span className="flex items-center gap-1.5 text-muted-foreground font-gc-reg">
            <span className="w-2.5 h-2.5 rounded-full inline-block bg-red-500" />
            Fail
          </span>
        </div>
      </div>
    </div>
  );
}

function LLMJudgeView({ bd }: { bd: CriterionBreakdown }) {
  const scores = bd.score_distribution || [];
  const totalScored = scores.reduce((s, i) => s + i.count, 0);

  if (totalScored === 0) {
    return (
      <span className="text-sm text-muted-foreground font-gc-reg">
        No evaluation data
      </span>
    );
  }

  const weightedSum = scores.reduce((s, i) => s + i.score * i.count, 0);
  const avgScore = weightedSum / totalScored;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-6">
        <div className="space-y-1">
          <div className="text-xs text-muted-foreground font-gc-reg uppercase tracking-wider">
            Avg Score
          </div>
          <div className="text-2xl font-gc-demi-bold text-foreground">
            {avgScore.toFixed(2)} / 5
          </div>
          <div className="text-xs text-muted-foreground font-gc-reg">
            {totalScored.toLocaleString()} evaluated
          </div>
        </div>
        <div className="space-y-1">
          <div className="text-xs text-muted-foreground font-gc-reg uppercase tracking-wider">
            Perfect (5/5)
          </div>
          <div className="text-2xl font-gc-demi-bold text-foreground">
            {bd.pass_count.toLocaleString()}
          </div>
          <div className="text-xs text-muted-foreground font-gc-reg">
            {totalScored > 0
              ? `${((bd.pass_count / totalScored) * 100).toFixed(1)}%`
              : "—"}
          </div>
        </div>
        <div className="space-y-1">
          <div className="text-xs text-muted-foreground font-gc-reg uppercase tracking-wider">
            Poor (1/5)
          </div>
          <div className="text-2xl font-gc-demi-bold text-foreground">
            {bd.fail_count.toLocaleString()}
          </div>
          <div className="text-xs text-muted-foreground font-gc-reg">
            {totalScored > 0
              ? `${((bd.fail_count / totalScored) * 100).toFixed(1)}%`
              : "—"}
          </div>
        </div>
      </div>

      {/* Score distribution bar */}
      <div className="space-y-3">
        <div className="flex rounded-md overflow-hidden h-6">
          {SCORE_META.map(({ score, label, color }) => {
            const entry = scores.find((i) => i.score === score);
            const count = entry?.count || 0;
            const pct = (count / totalScored) * 100;
            if (pct === 0) return null;
            return (
              <Tooltip
                key={score}
                title={`${label}: ${count.toLocaleString()} (${pct.toFixed(1)}%)`}
              >
                <div
                  className="h-full transition-all duration-300"
                  style={{ width: `${pct}%`, backgroundColor: color }}
                />
              </Tooltip>
            );
          })}
        </div>
        <div className="flex items-center justify-center gap-4 flex-wrap">
          {SCORE_META.map(({ score, label, color }) => {
            const entry = scores.find((i) => i.score === score);
            if (!entry || entry.count === 0) return null;
            return (
              <span
                key={score}
                className="flex items-center gap-1.5 text-xs text-muted-foreground font-gc-reg"
              >
                <span
                  className="w-2.5 h-2.5 rounded-full inline-block"
                  style={{ backgroundColor: color }}
                />
                {label}
              </span>
            );
          })}
        </div>
      </div>

      {/* Score table */}
      <table className="w-full">
        <thead>
          <tr className="border-b border-border">
            <th className="text-left text-xs font-gc-med text-muted-foreground uppercase tracking-wider py-2">
              Score
            </th>
            <th className="text-right text-xs font-gc-med text-muted-foreground uppercase tracking-wider py-2">
              Count
            </th>
            <th className="text-right text-xs font-gc-med text-muted-foreground uppercase tracking-wider py-2">
              Percentage
            </th>
            <th className="text-left text-xs font-gc-med text-muted-foreground uppercase tracking-wider py-2 pl-4 w-1/4">
              Distribution
            </th>
          </tr>
        </thead>
        <tbody>
          {SCORE_META.map(({ score, label, color }) => {
            const entry = scores.find((i) => i.score === score);
            const count = entry?.count || 0;
            const pct = (count / totalScored) * 100;
            return (
              <tr
                key={score}
                className="border-b border-border last:border-0"
              >
                <td className="py-2.5">
                  <div className="flex items-center gap-2">
                    <span
                      className="w-2.5 h-2.5 rounded-full inline-block flex-shrink-0"
                      style={{ backgroundColor: color }}
                    />
                    <span className="text-sm font-gc-med text-foreground">
                      {label}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      ({score}/5)
                    </span>
                  </div>
                </td>
                <td className="py-2.5 text-right text-sm font-gc-reg text-foreground">
                  {count.toLocaleString()}
                </td>
                <td className="py-2.5 text-right text-sm font-gc-reg text-foreground">
                  {pct.toFixed(1)}%
                </td>
                <td className="py-2.5 pl-4">
                  <div className="w-full bg-muted rounded-full h-2">
                    <div
                      className="h-2 rounded-full transition-all duration-300"
                      style={{ width: `${pct}%`, backgroundColor: color }}
                    />
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

const TestRunCriteriaBreakdown: React.FC<Props> = ({ breakdowns }) => {
  const [activeIdx, setActiveIdx] = useState(0);

  useEffect(() => {
    if (activeIdx >= breakdowns.length) {
      setActiveIdx(0);
    }
  }, [breakdowns.length]);

  if (!breakdowns || breakdowns.length === 0) return null;

  const active = breakdowns[activeIdx];
  const isDeterministic = DETERMINISTIC_TYPES.has(active.criteria_type);

  return (
    <Card className="border-border">
      <CardContent className="p-8">
        <h2 className="text-xl font-gc-med text-foreground mb-6">
          Per-Criterion Results
        </h2>

        {/* Criterion tabs */}
        <div className="flex items-center gap-2 mb-6 flex-wrap">
          {breakdowns.map((bd, idx) => (
            <button
              key={bd.criteria_id ?? bd.criteria_name}
              onClick={() => setActiveIdx(idx)}
              className={cn(
                "px-3 py-1.5 rounded-md text-xs font-gc-med transition-colors border",
                idx === activeIdx
                  ? "bg-maitai-green/20 text-maitai-green border-maitai-green/40"
                  : "text-muted-foreground border-border hover:text-foreground hover:bg-muted/50"
              )}
            >
              {bd.criteria_name}
              <span className="ml-1.5 text-muted-foreground font-gc-reg">
                ({bd.total_evaluated})
              </span>
            </button>
          ))}
        </div>

        {/* Active criterion view */}
        {isDeterministic ? (
          <DeterministicView bd={active} />
        ) : (
          <LLMJudgeView bd={active} />
        )}
      </CardContent>
    </Card>
  );
};

export default TestRunCriteriaBreakdown;
